import { useEffect, useState } from "react";
import { type ApiVersionResponse, fetchApiVersion, getCachedApiVersion } from "@/services/apiVersion";

interface UseApiVersionResult {
  data: ApiVersionResponse | null;
  isLoading: boolean;
  error: boolean;
}

/**
 * Fetches and caches the API version from GET /version.
 * Returns cached data immediately if available, otherwise fetches on mount.
 */
export const useApiVersion = (): UseApiVersionResult => {
  const [data, setData] = useState<ApiVersionResponse | null>(() => getCachedApiVersion());
  const [isLoading, setIsLoading] = useState(!getCachedApiVersion());
  const [error, setError] = useState(false);

  useEffect(() => {
    // If we already have cached data, skip the fetch
    if (data) return;

    let cancelled = false;

    const load = async () => {
      const result = await fetchApiVersion();
      if (cancelled) return;

      if (result) {
        setData(result);
      } else {
        setError(true);
      }
      setIsLoading(false);
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [data]);

  return { data, isLoading, error };
};
